import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { Newspaper } from "lucide-react";
import NewsCard from "@/components/news/NewsCard";
import FacebookEmbed from "@/components/news/FacebookEmbed";
import { pushEvent } from "@/lib/gtm";

const PAGE = 9;

export default function NewsAndUpdates() {
  const [tab, setTab] = useState("news");
  const [posts, setPosts] = useState(null);
  const [embeds, setEmbeds] = useState(null);
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    document.title = "News & Updates | Seisfra Philippines";
    pushEvent("page_view_spa", { page_type: "news_listing", page_path: "/news", page_title: "News & Updates" });
    base44.entities.NewsPost.filter({ status: "published" }, "-published_date", 500).then(setPosts).catch(() => setPosts([]));
    base44.entities.SocialMediaEmbed.filter({ status: "published" }, "display_order", 200).then(setEmbeds).catch(() => setEmbeds([]));
  }, []);

  const spinner = (
    <div className="py-20 flex justify-center">
      <div className="w-8 h-8 border-4 border-gray-200 border-t-[#E4001C] rounded-full animate-spin" />
    </div>
  );

  return (
    <>
      <section className="bg-[#0A0A0A] pt-40 pb-28 clip-diagonal-bottom relative overflow-hidden">
        <div className="absolute top-0 right-0 h-full w-40 bg-[#E4001C]/70 hidden md:block" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <p className="font-tech font-semibold uppercase tracking-[0.25em] text-[#E4001C] text-sm mb-4">Stay Informed</p>
          <h1 className="font-heading font-extrabold uppercase text-white text-5xl md:text-7xl tracking-wide">News & Updates</h1>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2 mb-10">
            {[{ key: "news", label: "News" }, { key: "social", label: "Social Media" }].map((t) => (
              <button key={t.key} onClick={() => { setTab(t.key); pushEvent("news_tab_click", { tab: t.key }); }}
                className={`font-tech font-semibold uppercase tracking-widest text-xs px-5 py-3 border transition-colors ${
                  tab === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-gray-300 text-[#171717] hover:border-gray-400"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "news" && (
            !posts ? spinner : posts.length === 0 ? (
              <div className="py-24 text-center">
                <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="font-tech uppercase tracking-widest text-sm text-gray-400">No news articles yet — check back soon.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.slice(0, visible).map((p) => <NewsCard key={p.id} post={p} />)}
                </div>
                {posts.length > visible && (
                  <div className="flex justify-center mt-12">
                    <button onClick={() => { setVisible(visible + PAGE); pushEvent("news_load_more", { shown_before: visible }); }}
                      className="border border-[#0A0A0A] hover:border-[#E4001C] hover:text-[#E4001C] font-tech font-semibold uppercase tracking-widest text-sm px-8 py-4 transition-colors">
                      Load More
                    </button>
                  </div>
                )}
              </>
            )
          )}

          {tab === "social" && (
            !embeds ? spinner : embeds.length === 0 ? (
              <div className="py-24 text-center">
                <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="font-tech uppercase tracking-widest text-sm text-gray-400">No posts yet — check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
                {embeds.map((e) => <FacebookEmbed key={e.id} url={e.facebook_post_url} />)}
              </div>
            )
          )}
        </div>
      </section>
    </>
  );
}